import { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { HANDOFF_LATEST_MATCHES_KEY } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { SEARCHER_TYPE_TO_MATCH_FOR } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { TriageSearch } from '@lyrahealth-inc/shared-app-logic/src/models/searchForCare/SearchForCare'

import { getGlobalIsHydrated } from '../../../data/appGlobals/appGlobalsSelectors'
import { useAppDispatch } from '../../../data/storeConfiguration/store'
import { clearDirectPathState } from '../../appointmentDashboard/data/appointmentDashboardActions'
import { setLatestMatchesSearch } from '../../onboard/data/onboardDataActions'
import { updateTriageDemographics } from '../../onboard/data/triage-demographics/triage-demographics-actions'
import { setSelectedTreatmentOption } from '../../onboard/treatmentOptions/data/treatmentOptionsActions'
import { setSelectedSearchId, upsertTriageSearch } from '../data/triageActions'

/**
 * Restores the Recent Matches search context that 1Lyra hands off before a full page load into an LW provider
 * page, so bookings stay attributed to that triage search. Waits for rehydration because redux-persist merges
 * the persisted triage state over anything dispatched before it, which could restore a stale selectedSearchId.
 */
export const use1LyraLatestMatchesHandoff = () => {
  const dispatch = useAppDispatch()
  const isStoreHydrated = useSelector(getGlobalIsHydrated)

  useEffect(() => {
    if (!isStoreHydrated) return

    const rawHandoff = sessionStorage.getItem(HANDOFF_LATEST_MATCHES_KEY)
    if (rawHandoff === null) return
    sessionStorage.removeItem(HANDOFF_LATEST_MATCHES_KEY)

    try {
      const { search, selectedTreatmentOption } = JSON.parse(rawHandoff) as {
        search?: TriageSearch
        selectedTreatmentOption?: string | null
      }
      if (!search?.id) return

      const isChildSearch = Boolean(search.body?.child_lyra_id || search.body?.childLyraId)

      dispatch(clearDirectPathState())
      dispatch(upsertTriageSearch(search))
      dispatch(setSelectedSearchId(search.id))
      dispatch(setLatestMatchesSearch(search))
      dispatch(
        updateTriageDemographics(
          'matchFor',
          isChildSearch ? SEARCHER_TYPE_TO_MATCH_FOR.OTHER_CHILD : SEARCHER_TYPE_TO_MATCH_FOR.SELF,
        ),
      )
      if (selectedTreatmentOption) {
        dispatch(setSelectedTreatmentOption(selectedTreatmentOption))
      }
    } catch {
      // Ignore malformed handoff data; LW falls back to its persisted state.
    }
  }, [dispatch, isStoreHydrated])
}
